"use client";

import Link from "next/link";
import { useState } from "react";

type ActionLink = {
  label: string;
  href: string;
  variant?: "primary" | "secondary";
};

type RelatedLink = {
  title: string;
  href: string;
  note: string;
};

export type WorkspaceSplitItem = {
  id: string;
  label: string;
  eyebrow: string;
  title: string;
  description: string;
  context: string;
  stats?: string[];
  bullets?: string[];
  primaryAction: ActionLink;
  secondaryAction?: ActionLink;
  related: RelatedLink[];
};

type Props = {
  sectionLabel: string;
  title: string;
  intro: string;
  items: WorkspaceSplitItem[];
  theme: "market" | "money-flow" | "scanner";
};

export default function WorkspaceSplitNavigator({
  sectionLabel,
  title,
  intro,
  items,
  theme,
}: Props) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");
  const activeItem = items.find((item) => item.id === activeId) ?? items[0];

  if (!activeItem) return null;

  return (
    <section className={`workspaceSplit workspaceSplit--${theme}`}>
      <aside className="workspaceSplitRail">
        <div className="workspaceSplitRailHeader">
          <div className="workspaceEyebrow">{sectionLabel}</div>
          <h2>{title}</h2>
          <p>{intro}</p>
        </div>

        <div className="workspaceSplitList">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={`workspaceSplitItem${item.id === activeItem.id ? " is-active" : ""}`}
              onClick={() => setActiveId(item.id)}
            >
              <span className="workspaceSplitItemIndex">{String(index + 1).padStart(2, "0")}</span>
              <span className="workspaceSplitItemBody">
                <span className="workspaceSplitItemLabel">{item.label}</span>
                <span className="workspaceSplitItemMeta">{item.context}</span>
              </span>
            </button>
          ))}
        </div>
      </aside>

      <article className="workspaceSplitDetail">
        <div className="workspaceSplitHero">
          <div className="workspaceSplitHeroTop">
            <div>
              <div className="workspaceEyebrow">{activeItem.eyebrow}</div>
              <div className="workspaceSplitCurrent">
                <span className="workspaceSplitCurrentLabel">Selected</span>
                <strong>{activeItem.label}</strong>
              </div>
              <div className="workspaceSplitStartCue">Open this first</div>
            </div>
            <span className="workspaceSplitContextPill">{activeItem.context}</span>
          </div>

          <div className="workspaceSplitHeroBody">
            <div className="workspaceSplitHeroContent">
              <h3>{activeItem.title}</h3>
              <p>{activeItem.description}</p>

              {activeItem.stats && activeItem.stats.length > 0 ? (
                <div className="workspaceSplitStats">
                  {activeItem.stats.map((stat) => (
                    <span key={stat} className="workspaceSplitStat">
                      {stat}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="workspaceSplitActionPanel">
              <div className="workspaceEyebrow">Best next step</div>
              <div className="workspaceSplitActionLead">
                <span className="workspaceSplitActionLeadLabel">Recommended opening page</span>
                <strong>{activeItem.primaryAction.label}</strong>
              </div>
              <div className="workspaceSplitActions">
                <Link className="primaryButton workspaceSplitPrimaryAction" href={activeItem.primaryAction.href}>
                  {activeItem.primaryAction.label}
                </Link>
              </div>
              {activeItem.secondaryAction ? (
                <div className="workspaceSplitSecondaryBlock">
                  <span className="workspaceSplitSecondaryLabel">Alternate path</span>
                  <Link className="ghostButton workspaceSplitSecondaryAction" href={activeItem.secondaryAction.href}>
                    {activeItem.secondaryAction.label}
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="workspaceSplitBody">
          <section className="workspaceSplitPanel">
            <div className="workspaceEyebrow">How to use it</div>
            <div className="workspaceSplitBulletList">
              {(activeItem.bullets ?? []).map((bullet) => (
                <div key={bullet} className="workspaceSplitBullet">
                  <span className="workspaceSplitBulletDot" />
                  <span>{bullet}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="workspaceSplitPanel">
            <div className="workspaceEyebrow">Related pages</div>
            <div className="workspaceSplitRelatedGrid">
              {activeItem.related.map((related) => (
                <Link
                  key={related.title}
                  href={related.href}
                  className={`workspaceSplitRelatedCard${related.href === activeItem.primaryAction.href ? " is-primary" : ""}`}
                >
                  <span className="workspaceSplitRelatedEyebrow">
                    {related.href === activeItem.primaryAction.href ? "Start with this" : "Related page"}
                  </span>
                  <strong>{related.title}</strong>
                  <p>{related.note}</p>
                  <span className="workspaceTileLink">Open page →</span>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </article>
    </section>
  );
}
